import React from 'react';
import { Box, List, ListItem, ListItemIcon, ListItemText, Paper, Tooltip, Drawer, } from "@mui/material";
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';

// Admin Icons
import DashboardIcon from '@mui/icons-material/Dashboard';
import SchoolIcon from '@mui/icons-material/School';
import ViewAgendaIcon from '@mui/icons-material/ViewAgenda';
import CollectionsBookmarkIcon from '@mui/icons-material/CollectionsBookmark';
import QuizIcon from '@mui/icons-material/Quiz';
import CodeOffIcon from '@mui/icons-material/CodeOff';
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import HistoryIcon from '@mui/icons-material/History';
// Student Icons
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
// college Icon
import PersonIcon from '@mui/icons-material/Person';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import GroupsIcon from '@mui/icons-material/Groups';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';

const Sidebar = ({ isSmallScreen, sidebarOpen, toggleSidebar }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();

    // Define menu items for each role
    const adminMenuItems = [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin-dashboard' },
        { text: 'Add Institute', icon: <SchoolIcon />, path: '/add-college' },
        // { text: 'Add Department', icon: <BusinessIcon />, path: '/add-department' },
        { text: 'Add Course', icon: <MenuBookIcon />, path: '/add-course' },
        { text: 'Add Section', icon: <ViewAgendaIcon />, path: '/add-section' },
        { text: 'Add Lesson', icon: <CollectionsBookmarkIcon />, path: '/add-lesson' },
        { text: 'Add Quiz', icon: <QuizIcon />, path: '/add-quiz' },
        { text: 'Student', icon: <AssignmentIndIcon />, path: '/users' },
        { text: 'Manage Voucher', icon: <CodeOffIcon />, path: '/voucher' },
        { text: 'Manage Roles', icon: <AssignmentIndIcon />, path: '/add-role' },
        { text: 'Assign Course', icon: <AssignmentTurnedInIcon />, path: '/admin/assign-course' },
        { text: 'Course Assignment', icon: <GroupsIcon />, path: '/admin/course-assignment' },
        { text: 'Notifications', icon: <NotificationsActiveIcon />, path: '/admin/notification-center' },
        { text: 'Audit Log', icon: <HistoryIcon />, path: '/admin/audit-log-details' },
        { text: 'Help & Support', icon: <HelpOutlineIcon />, path: '/admin/support-ticket' },
        // { text: 'Settings', icon: <SettingsIcon />, path: '/admin/settings' }
    ];

    const instructorMenuItems = [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' }, 
    ];

    const studentMenuItems = [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
        // { text: 'Courses', icon: <StoreIcon />, path: '/course' },
        { text: 'My Courses', icon: <MenuBookIcon />, path: '/my-courses' },
        // { text: 'Assignments', icon: <AssignmentIndIcon />, path: '/student/assignments' },
        // { text: 'Progress', icon: <TimelineIcon />, path: '/student/progress' }
    ];

    const institutionMenuItems = [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/institute-dashboard' },
        { text: 'View Students', icon: <PersonIcon />, path: '/add-student' },
        // { text: 'Add Instructors', icon: <PersonAddAlt1Icon />, path: '/add-instructors' },
        // { text: 'Add Department', icon: <BusinessIcon />, path: '/institution/add-department' },
        // { text: 'Courses', icon: <StoreIcon />, path: '/course' },
        { text: 'My Courses', icon: <MenuBookIcon />, path: '/my-courses' },
        // { text: 'Assign Course', icon: <AssignmentTurnedInIcon />, path: '/institution/assign-course' },
        { text: 'Help & Support', icon: <HelpOutlineIcon />, path: '/support-ticket' },
    ];

    // Get menu items based on user role
    const getMenuItems = () => {
        switch (user.role_id.role_name) {
            case 'Admin':
                return adminMenuItems;
            case 'Instructors':
                return instructorMenuItems;
            case 'Students':
                return studentMenuItems;
            case 'Institution':
                return institutionMenuItems;
            default:
                return [];
        }
    };

    const handleNavigate = (path) => {
        navigate(path);
        if (isSmallScreen) {
            toggleSidebar();
        }
    };

    const sidebarScrollSx = {
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': {
            width: 6,
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0, 0, 0, 0.25)',
            borderRadius: 8,
        },
        '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
        },
    };

    const SidebarContent = () => (
        <Box sx={sidebarScrollSx}>
            {/* Logo */}
            {/* <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2, }} >
                <Typography variant="h6" sx={{ opacity: sidebarOpen ? 1 : 0, transition: 'opacity 0.3s' }}>
                    LMS
                </Typography>
            </Box> */}

            <Box sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 2,
            }}>
                <Box
                    component="img"
                    src={sidebarOpen ? "/image/se-lms-logo.png" : "/image/se-lms-logo.png"}
                    alt="LMS Logo"
                    sx={{
                        height: sidebarOpen ? 40 : 32, // Adjust as needed
                        width: sidebarOpen ? 'auto' : 48, // For small logo, keep it square
                        paddingLeft: sidebarOpen ? 2 : 0,
                        opacity: 1,
                        transition: 'all 0.3s',
                        objectFit: 'contain'
                    }}
                />
            </Box>

            {/* Navigation */}
            <List sx={{ mt: 0 }}>
                {getMenuItems().map((item) => {
                    const isActive = location.pathname === item.path;
                    return (
                        <ListItem
                            key={item.text}
                            button="true"
                            onClick={() => handleNavigate(item.path)}
                            sx={{
                                mx: 1,
                                borderRadius: 1,
                                mb: 0.5,
                                bgcolor: isActive ? "#4B49AC" : "transparent",
                                "&:hover": {
                                    bgcolor: isActive ? "#4B49AC" : "action.hover", cursor: 'pointer'
                                },
                            }}
                        >
                            <Tooltip title={sidebarOpen ? "" : item.text} placement="right">
                                <ListItemIcon
                                    sx={{
                                        color: isActive ? "white" : "text.primary",
                                        minWidth: 40,
                                        justifyContent: "center",
                                    }}
                                >
                                    {item.icon}
                                </ListItemIcon>
                            </Tooltip>
                            {sidebarOpen && (
                                <ListItemText
                                    primary={item.text}
                                    sx={{
                                        color: isActive ? "white" : "text.primary",
                                        opacity: sidebarOpen ? 1 : 0,
                                        transition: 'opacity 0.3s',
                                    }}
                                />
                            )}
                        </ListItem>
                    );
                })}
            </List>
        </Box>
    );

    return (
        <>
            {isSmallScreen ? (
                <Drawer
                    anchor="left"
                    open={sidebarOpen}
                    onClose={toggleSidebar}
                    sx={{
                        '& .MuiDrawer-paper': {
                            width: sidebarOpen ? 280 : 80,
                            height: '100vh',
                            boxSizing: 'border-box',
                            transition: 'width 0.3s ease-in-out',
                            overflow: 'hidden',
                        },
                    }}
                >
                    <SidebarContent />
                </Drawer>
            ) : (
                <Paper
                    elevation={4}
                    sx={{
                        width: sidebarOpen ? 280 : 80,
                        height: "100vh",
                        position: "fixed",
                        top: 0,
                        left: 0,
                        zIndex: 1,
                        transition: 'width 0.3s ease-in-out',
                        overflow: 'hidden',
                    }}
                >
                    <SidebarContent />
                </Paper>
            )}
        </>
    );
};

export default Sidebar;
